/**
 * InsightPro™ — Creditors Page v1.0
 * Data: Creditors table — pre-calculated ageing buckets per supplier
 * Columns: ACCOUNT, NAME, TOTAL, CURRENT, DAYS_30, DAYS_60, DAYS_90,
 *          DAYS_120, DAYS_150, DAYS_180
 * Add your Creditors table to Power BI with the same structure as Debtors.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pbiQuery } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const GOLD  = 'var(--gold)';
const RED   = '#e05555';
const GREEN = '#1aad63';
const AMBER = '#d4a017';
const BLUE  = '#4a9eff';

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return isMobile;
}

function usePbi(dax) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await pbiQuery({ dax });
      setData(r?.rows || []);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [dax]);
  React.useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load };
}

const fmtR = n => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return `R ${(v/1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000)     return `R ${(v/1_000).toFixed(0)}K`;
  return `R ${v.toFixed(0)}`;
};

const fmtPct = (num, denom) => denom > 0 ? `${((num/denom)*100).toFixed(1)}%` : '0%';

const fmtDate = d => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-ZA', { day:'numeric', month:'short', year:'2-digit' }); }
  catch { return '—'; }
};

// ── Shared UI components ────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:40 }}>
      <div style={{ width:24, height:24, border:'3px solid rgba(196,146,42,0.2)', borderTop:'3px solid var(--gold)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function ErrorBox({ msg, onRetry }) {
  return (
    <div style={{ padding:'16px 20px', background:'rgba(224,85,85,0.07)', border:'1px solid rgba(224,85,85,0.2)', borderRadius:4, margin:16 }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:RED, marginBottom:4, letterSpacing:'0.1em' }}>DATA ERROR</div>
      <div style={{ fontSize:11, color:'var(--page-muted)', marginBottom:10 }}>{msg}</div>
      {onRetry && <button onClick={onRetry} style={{ fontFamily:'var(--font-mono)', fontSize:9, color:GOLD, background:'transparent', border:'1px solid var(--gold)', padding:'4px 12px', cursor:'pointer', borderRadius:2 }}>Retry</button>}
    </div>
  );
}

function KpiCard({ label, value, sub, subColor, alert }) {
  return (
    <div style={{ background:'var(--card)', border:`1px solid ${alert ? 'rgba(224,85,85,0.3)' : 'var(--card-border)'}`, borderRadius:4, padding:'16px 18px' }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--page-muted)', marginBottom:8 }}>{label}</div>
      <div style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:700, color: alert ? RED : 'var(--page-text)', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color: subColor || 'var(--page-muted)', marginTop:6 }}>{sub}</div>}
    </div>
  );
}

function Card({ children }) {
  return <div style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, overflow:'hidden', marginBottom:12 }}>{children}</div>;
}

function CardHeader({ title }) {
  return (
    <div style={{ padding:'12px 20px', borderBottom:'1px solid var(--card-border)' }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--page-muted)' }}>{title}</div>
    </div>
  );
}

function BucketBar({ data }) {
  const colors = { 'Current': GREEN, '30 Days': GOLD, '60 Days': AMBER, '90 Days': RED, '120 Days': RED, '150 Days': RED, '180+ Days': RED };
  return (
    <ResponsiveContainer width="100%" height={160}>
      <BarChart data={data} margin={{ top:4, right:0, bottom:0, left:0 }}>
        <XAxis dataKey="[Bucket]" tick={{ fontFamily:'var(--font-mono)', fontSize:8, fill:'var(--page-muted)' }} axisLine={false} tickLine={false}/>
        <YAxis hide/>
        <Tooltip formatter={v => fmtR(v)} contentStyle={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, fontFamily:'var(--font-mono)', fontSize:10 }}/>
        <Bar dataKey="[Amount]" radius={[2,2,0,0]}>
          {(data||[]).map((entry, i) => <Cell key={i} fill={colors[entry['[Bucket]']] || GOLD}/>)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── DAX queries ─────────────────────────────────────────────
const KPI_DAX = `EVALUATE ROW(
  "TotalBook",     CALCULATE(SUM('Creditors'[TOTAL]),   'Creditors'[TOTAL] > 0),
  "Current",       CALCULATE(SUM('Creditors'[CURRENT]), 'Creditors'[TOTAL] > 0),
  "Days30",        CALCULATE(SUM('Creditors'[DAYS_30]), 'Creditors'[TOTAL] > 0),
  "Days60",        CALCULATE(SUM('Creditors'[DAYS_60]), 'Creditors'[TOTAL] > 0),
  "Days90",        CALCULATE(SUM('Creditors'[DAYS_90]), 'Creditors'[TOTAL] > 0),
  "Days120",       CALCULATE(SUM('Creditors'[DAYS_120]),'Creditors'[TOTAL] > 0),
  "Days150",       CALCULATE(SUM('Creditors'[DAYS_150]),'Creditors'[TOTAL] > 0),
  "Days180Plus",   CALCULATE(SUM('Creditors'[DAYS_180]),'Creditors'[TOTAL] > 0),
  "Overdue60Plus", CALCULATE(SUM('Creditors'[DAYS_60]) + SUM('Creditors'[DAYS_90]) + SUM('Creditors'[DAYS_120]) + SUM('Creditors'[DAYS_150]) + SUM('Creditors'[DAYS_180]), 'Creditors'[TOTAL] > 0),
  "ActiveCreditors", CALCULATE(COUNTROWS('Creditors'), 'Creditors'[TOTAL] > 0)
)`;

const BUCKETS_DAX = `EVALUATE
  UNION(
    ROW("Bucket", "Current",   "Amount", CALCULATE(SUM('Creditors'[CURRENT]),  'Creditors'[TOTAL] > 0)),
    ROW("Bucket", "30 Days",   "Amount", CALCULATE(SUM('Creditors'[DAYS_30]),  'Creditors'[TOTAL] > 0)),
    ROW("Bucket", "60 Days",   "Amount", CALCULATE(SUM('Creditors'[DAYS_60]),  'Creditors'[TOTAL] > 0)),
    ROW("Bucket", "90 Days",   "Amount", CALCULATE(SUM('Creditors'[DAYS_90]),  'Creditors'[TOTAL] > 0)),
    ROW("Bucket", "120 Days",  "Amount", CALCULATE(SUM('Creditors'[DAYS_120]), 'Creditors'[TOTAL] > 0)),
    ROW("Bucket", "150 Days",  "Amount", CALCULATE(SUM('Creditors'[DAYS_150]), 'Creditors'[TOTAL] > 0)),
    ROW("Bucket", "180+ Days", "Amount", CALCULATE(SUM('Creditors'[DAYS_180]), 'Creditors'[TOTAL] > 0))
  )`;

const ACCOUNTS_DAX = `EVALUATE
  TOPN(50,
    FILTER(
      SELECTCOLUMNS('Creditors',
        "Account",    'Creditors'[ACCOUNT],
        "Name",       'Creditors'[NAME],
        "Total",      'Creditors'[TOTAL],
        "Current",    'Creditors'[CURRENT],
        "Days30",     'Creditors'[DAYS_30],
        "Days60",     'Creditors'[DAYS_60],
        "Days90",     'Creditors'[DAYS_90],
        "Days120",    'Creditors'[DAYS_120],
        "Days150",    'Creditors'[DAYS_150],
        "Days180",    'Creditors'[DAYS_180],
        "Overdue",    'Creditors'[DAYS_60] + 'Creditors'[DAYS_90] + 'Creditors'[DAYS_120] + 'Creditors'[DAYS_150] + 'Creditors'[DAYS_180]
      ),
      [Total] > 0
    ),
    [Total], DESC
  )
  ORDER BY [Total] DESC`;

// ── Main page ───────────────────────────────────────────────
export default function CreditorsPage() {
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get('search') ? 'accounts' : 'overview');

  const { data: kpiRaw,  loading: l0, error: e0, reload: r0 } = usePbi(KPI_DAX);
  const { data: buckets, loading: l1, error: e1, reload: r1 } = usePbi(BUCKETS_DAX);
  const { data: accounts,loading: l2, error: e2, reload: r2 } = usePbi(ACCOUNTS_DAX);

  const kpi = Array.isArray(kpiRaw) ? kpiRaw[0] : kpiRaw;
  const totalBook       = kpi?.['[TotalBook]']       || 0;
  const overdue60       = kpi?.['[Overdue60Plus]']   || 0;
  const days180         = kpi?.['[Days180Plus]']     || 0;
  const activeCreditors = kpi?.['[ActiveCreditors]'] || 0;
  const overduePct      = totalBook > 0 ? (overdue60 / totalBook) * 100 : 0;

  return (
    <div style={{ animation:'fadeUp 0.4s ease both' }}>

      {/* Page header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:10 }}>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase', color:'var(--page-muted)' }}>Creditors</div>
        <div style={{ display:'flex', gap:6 }}>
          {['overview','accounts'].map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              fontFamily:'var(--font-body)', fontSize:11, fontWeight: view===v ? 700 : 500,
              padding:'5px 14px', borderRadius:3, cursor:'pointer',
              border: view===v ? '1px solid var(--border-gold)' : '1px solid var(--border)',
              background: view===v ? 'var(--gold)' : 'transparent',
              color: view===v ? 'var(--void)' : 'var(--text-sec)',
            }}>{v === 'overview' ? 'Overview' : 'All Suppliers'}</button>
          ))}
        </div>
      </div>

      {view === 'overview' ? (
        <OverviewView kpi={kpi} l0={l0} e0={e0} r0={r0} buckets={buckets} l1={l1} e1={e1} r1={r1}
          accounts={accounts} l2={l2} e2={e2} r2={r2}
          totalBook={totalBook} overdue60={overdue60} days180={days180}
          activeCreditors={activeCreditors} overduePct={overduePct}
          isMobile={isMobile} />
      ) : (
        <AccountsView accounts={accounts} loading={l2} error={e2} reload={r2}
          totalBook={totalBook} isMobile={isMobile} />
      )}
    </div>
  );
}

// ── Overview ────────────────────────────────────────────────
function OverviewView({ kpi, l0, e0, r0, buckets, l1, e1, r1, accounts, l2, e2, r2,
  totalBook, overdue60, days180, activeCreditors, overduePct, isMobile }) {

  const cols = isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)';

  return (
    <>
      {/* KPI strip */}
      <div style={{ display:'grid', gridTemplateColumns:cols, gap:10, marginBottom:16 }}>
        {l0 ? [0,1,2,3].map(i => <div key={i} style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, height:90 }}><Spinner/></div>)
          : e0 ? <div style={{ gridColumn:'1/-1' }}><ErrorBox msg={e0} onRetry={r0}/></div>
          : <>
            <KpiCard label="Total Creditors Book"   value={fmtR(totalBook)}   sub={`${activeCreditors} active suppliers`} />
            <KpiCard label="Overdue 60+ Days"        value={fmtR(overdue60)}   sub={`${overduePct.toFixed(1)}% of book`} subColor={overduePct > 30 ? RED : overduePct > 15 ? AMBER : GREEN} alert={overduePct > 30} />
            <KpiCard label="180+ Days"               value={fmtR(days180)}     sub={fmtPct(days180, totalBook) + ' of book'} subColor={days180 > 0 ? RED : GREEN} alert={days180 > 0} />
            <KpiCard label="Current"                 value={fmtR(kpi?.['[Current]'] || 0)} sub="within terms" subColor={GREEN} />
          </>
        }
      </div>

      {/* Ageing chart + top suppliers */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1.4fr)', gap:12, marginBottom:12 }}>
        <Card>
          <CardHeader title="Ageing Bucket Analysis" />
          <div style={{ padding:'16px 20px 20px' }}>
            {l1 ? <Spinner/> : e1 ? <ErrorBox msg={e1} onRetry={r1}/>
              : buckets?.length > 0 ? (
                <>
                  <BucketBar data={buckets} />
                  <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:6 }}>
                    {(buckets || []).map((b, i) => {
                      const pct = totalBook > 0 ? (b['[Amount]'] / totalBook) * 100 : 0;
                      const isOverdue = !['Current','30 Days'].includes(b['[Bucket]']);
                      return (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:8, height:8, borderRadius:2, background: isOverdue ? (pct > 15 ? RED : AMBER) : GREEN, flexShrink:0 }}/>
                          <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', width:70 }}>{b['[Bucket]']}</span>
                          <div style={{ flex:1, height:4, background:'var(--card-border)', borderRadius:2 }}>
                            <div style={{ height:'100%', borderRadius:2, width:`${Math.min(100,pct)}%`, background: isOverdue ? (pct > 15 ? RED : AMBER) : GREEN }}/>
                          </div>
                          <span style={{ fontFamily:'var(--font-display)', fontSize:12, fontWeight:600, color: isOverdue ? (pct > 10 ? RED : 'var(--page-text)') : 'var(--page-text)', minWidth:70, textAlign:'right' }}>{fmtR(b['[Amount]'])}</span>
                          <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', minWidth:38, textAlign:'right' }}>{pct.toFixed(1)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : <div style={{ padding:24, textAlign:'center', color:'var(--page-muted)', fontFamily:'var(--font-mono)', fontSize:11 }}>No data — add Creditors table to Power BI</div>
            }
          </div>
        </Card>

        <Card>
          <CardHeader title="Top Overdue Suppliers (60+ Days)" />
          {l2 ? <Spinner/> : e2 ? <ErrorBox msg={e2} onRetry={r2}/>
            : (accounts || []).filter(a => a['[Overdue]'] > 0).slice(0, 8).map((a, i) => {
              const overduePctAcc = a['[Total]'] > 0 ? (a['[Overdue]'] / a['[Total]']) * 100 : 0;
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 20px', borderBottom:'1px solid var(--card-border)' }}>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', width:18, flexShrink:0 }}>{String(i+1).padStart(2,'0')}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, color:'var(--page-text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginBottom:2 }}>{a['[Name]']}</div>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)' }}>
                      Account: {a['[Account]']} · Total: {fmtR(a['[Total]'])}
                    </div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontFamily:'var(--font-display)', fontSize:13, fontWeight:700, color:RED }}>{fmtR(a['[Overdue]'])}</div>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:RED }}>{overduePctAcc.toFixed(0)}% overdue</div>
                  </div>
                </div>
              );
            })
          }
        </Card>
      </div>
    </>
  );
}

// ── All Suppliers ────────────────────────────────────────────
function AccountsView({ accounts, loading, error, reload, totalBook, isMobile }) {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState('Total');
  const navigate = useNavigate();

  // Auto-switch to accounts view if search param present
  useEffect(() => {
    if (searchParams.get('search')) setSearch(searchParams.get('search'));
  }, [searchParams]);

  const filtered = (accounts || [])
    .filter(a => !search || a['[Name]'].toLowerCase().includes(search.toLowerCase()) || a['[Account]'].toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (b[`[${sortBy}]`] || 0) - (a[`[${sortBy}]`] || 0));

  const sortBtn = (label, key) => (
    <button onClick={() => setSortBy(key)} style={{
      fontFamily:'var(--font-mono)', fontSize:9, padding:'4px 10px', cursor:'pointer', borderRadius:2,
      border: sortBy===key ? '1px solid var(--border-gold)' : '1px solid var(--border)',
      background: sortBy===key ? 'var(--gold)' : 'transparent',
      color: sortBy===key ? 'var(--void)' : 'var(--text-muted)',
    }}>{label}</button>
  );

  return (
    <Card>
      <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--card-border)', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search supplier or account..."
          style={{ flex:1, minWidth:180, padding:'7px 12px', background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:3, color:'var(--page-text)', fontFamily:'var(--font-body)', fontSize:12, outline:'none' }}
        />
        <div style={{ display:'flex', gap:5, flexShrink:0 }}>
          {sortBtn('Total','Total')}
          {sortBtn('Overdue','Overdue')}
          {sortBtn('180d','Days180')}
        </div>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', flexShrink:0 }}>{filtered.length} suppliers</span>
      </div>

      {loading ? <Spinner/> : error ? <ErrorBox msg={error} onRetry={reload}/> : (
        <>
          {!isMobile && (
            <div style={{ display:'grid', gridTemplateColumns:'180px 1fr 90px 80px 80px 80px 80px 80px 90px', gap:8, padding:'8px 16px', background:'var(--void)', borderBottom:'1px solid var(--card-border)' }}>
              {['ACCOUNT','NAME','TOTAL','CURRENT','30D','60D','90D','120D+','OVERDUE'].map(h => (
                <div key={h} style={{ fontFamily:'var(--font-mono)', fontSize:8, letterSpacing:'0.1em', color:'var(--page-muted)', textAlign: h==='NAME' ? 'left' : 'right' }}>{h}</div>
              ))}
            </div>
          )}

          {filtered.map((a, i) => {
            const overdue = a['[Overdue]'] || 0;
            const days120plus = (a['[Days120]']||0) + (a['[Days150]']||0) + (a['[Days180]']||0);
            const isRed = overdue / (a['[Total]']||1) > 0.5;
            return isMobile ? (
              <div key={i} style={{ padding:'12px 16px', borderBottom:'1px solid var(--card-border)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'var(--page-text)' }}>{a['[Name]']}</div>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', marginTop:2, display:'flex', alignItems:'center', gap:8 }}>
                      {a['[Account]']}
                      <button onClick={() => navigate(`/stock-optimisation?tab=suppliers&search=${encodeURIComponent(a['[Name]'] || '')}`)}
                        style={{ background:'none', border:'none', color:'var(--gold)', cursor:'pointer', fontSize:9, padding:0 }}>📦 Orders</button>
                    </div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontFamily:'var(--font-display)', fontSize:14, fontWeight:700, color:'var(--page-text)' }}>{fmtR(a['[Total]'])}</div>
                    {overdue > 0 && <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:RED }}>{fmtR(overdue)} overdue</div>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {[['Current',a['[Current]'],GREEN],['30d',a['[Days30]'],GOLD],['60d',a['[Days60]'],AMBER],['90d',a['[Days90]'],RED],['120d+',days120plus,RED]].filter(([,v]) => v > 0).map(([l,v,c]) => (
                    <div key={l} style={{ fontFamily:'var(--font-mono)', fontSize:9, color:c }}>{l}: {fmtR(v)}</div>
                  ))}
                </div>
              </div>
            ) : (
              <div key={i} style={{ display:'grid', gridTemplateColumns:'180px 1fr 90px 80px 80px 80px 80px 80px 90px', gap:8, padding:'9px 16px', borderBottom:'1px solid var(--card-border)', background: i%2===0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a['[Account]']}</div>
                <div style={{ fontSize:12, color:'var(--page-text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a['[Name]']}</div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:12, fontWeight:600, color:'var(--page-text)', textAlign:'right' }}>{fmtR(a['[Total]'])}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:GREEN, textAlign:'right' }}>{fmtR(a['[Current]'])}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:a['[Days30]']>0 ? GOLD : 'var(--page-muted)', textAlign:'right' }}>{fmtR(a['[Days30]'])}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:a['[Days60]']>0 ? AMBER : 'var(--page-muted)', textAlign:'right' }}>{fmtR(a['[Days60]'])}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:a['[Days90]']>0 ? RED : 'var(--page-muted)', textAlign:'right' }}>{fmtR(a['[Days90]'])}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:days120plus>0 ? RED : 'var(--page-muted)', textAlign:'right' }}>{fmtR(days120plus)}</div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:12, fontWeight:700, color:overdue>0 ? (isRed ? RED : AMBER) : 'var(--page-muted)', textAlign:'right' }}>{fmtR(overdue)}</div>
              </div>
            );
          })}
        </>
      )}
    </Card>
  );
}
